/**
 * War Room Audio Engine — Procedural God Theme Music + SFX
 * Uses Web Audio API to synthesize unique ambient themes per god
 * and cinematic sound effects for key moments.
 */

let audioCtx = null;

function getCtx() {
  if (!audioCtx) {
    audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  }
  if (audioCtx.state === 'suspended') audioCtx.resume();
  return audioCtx;
}

// ═══════════════════════════════════════════════════════════════
// GOD THEME DEFINITIONS — each god has a unique musical identity
// ═══════════════════════════════════════════════════════════════

const GOD_THEMES = {
  athena: {
    // Strategic, contemplative — subtle low pad
    notes: [220, 261.63, 329.63, 293.66],
    waveform: 'sine',
    filterFreq: 500,
    filterQ: 0.8,
    attack: 1.0,
    release: 3.0,
    gain: 0.04,
    detune: -10,
  },
  hermes: {
    // Light, airy, gentle sparkle
    notes: [523.25, 659.25, 783.99, 880],
    waveform: 'sine',
    filterFreq: 800,
    filterQ: 0.3,
    attack: 0.8,
    release: 2.0,
    gain: 0.03,
    detune: 5,
  },
  apollo: {
    // Warm, radiant, golden pad
    notes: [392, 493.88, 587.33, 440],
    waveform: 'sine',
    filterFreq: 700,
    filterQ: 0.5,
    attack: 1.0,
    release: 3.5,
    gain: 0.035,
    detune: 0,
  },
  eris: {
    // Dark, mysterious, low drone
    notes: [233.08, 311.13, 369.99, 277.18],
    waveform: 'sine',
    filterFreq: 350,
    filterQ: 1.0,
    attack: 0.8,
    release: 2.5,
    gain: 0.035,
    detune: -5,
  },
  themis: {
    // Precise, measured, judicial — clean soft tones
    notes: [349.23, 440, 523.25, 349.23],
    waveform: 'sine',
    filterFreq: 600,
    filterQ: 0.5,
    attack: 0.8,
    release: 2.5,
    gain: 0.035,
    detune: 0,
  },
  calliope: {
    // Beautiful, flowing, creative
    notes: [493.88, 587.33, 698.46, 659.25],
    waveform: 'sine',
    filterFreq: 900,
    filterQ: 0.4,
    attack: 0.8,
    release: 2.5,
    gain: 0.03,
    detune: -5,
  },
  hestia: {
    // Warm, nurturing, gentle
    notes: [261.63, 329.63, 392, 349.23],
    waveform: 'sine',
    filterFreq: 600,
    filterQ: 0.3,
    attack: 1.0,
    release: 3.0,
    gain: 0.03,
    detune: 0,
  },
  plutus: {
    // Elegant, deep, calculated — low ambient
    notes: [146.83, 174.61, 220, 196],
    waveform: 'sine',
    filterFreq: 400,
    filterQ: 0.5,
    attack: 1.0,
    release: 3.0,
    gain: 0.04,
    detune: -3,
  },
  nike: {
    // Triumphant, bright but soft
    notes: [440, 554.37, 659.25, 880],
    waveform: 'sine',
    filterFreq: 700,
    filterQ: 0.5,
    attack: 0.8,
    release: 2.0,
    gain: 0.03,
    detune: 0,
  },
  metis: {
    // Deep, contemplative, cosmic drone
    notes: [110, 130.81, 164.81, 146.83],
    waveform: 'sine',
    filterFreq: 350,
    filterQ: 0.8,
    attack: 1.2,
    release: 4.0,
    gain: 0.04,
    detune: -8,
  },
  ares: {
    // Intense but restrained — deep rumble
    notes: [130.81, 155.56, 185, 164.81],
    waveform: 'sine',
    filterFreq: 300,
    filterQ: 0.8,
    attack: 0.8,
    release: 2.5,
    gain: 0.04,
    detune: -3,
  },
  dionysus: {
    // Warm, mellow, inviting
    notes: [392, 440, 523.25, 493.88],
    waveform: 'sine',
    filterFreq: 800,
    filterQ: 0.5,
    attack: 0.6,
    release: 2.0,
    gain: 0.03,
    detune: 3,
  },
};

// Active theme tracking
let activeTheme = null;
let activeNodes = [];

/**
 * Play a god's theme music softly
 * Returns a stop function
 */
export function playGodTheme(godId, volume = 0.3) {
  stopGodTheme(); // stop any current theme

  const theme = GOD_THEMES[godId];
  if (!theme) return () => {};

  const ctx = getCtx();
  const now = ctx.currentTime;
  const masterGain = ctx.createGain();
  masterGain.gain.setValueAtTime(0, now);
  masterGain.gain.linearRampToValueAtTime(theme.gain * volume, now + theme.attack);
  masterGain.connect(ctx.destination);

  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = theme.filterFreq;
  filter.Q.value = theme.filterQ;
  filter.connect(masterGain);

  const nodes = [];

  // Play chord — all notes together as a pad
  theme.notes.forEach((freq, i) => {
    const osc = ctx.createOscillator();
    osc.type = theme.waveform;
    osc.frequency.value = freq;
    osc.detune.value = theme.detune + (i * 3);

    const noteGain = ctx.createGain();
    noteGain.gain.value = 1.0 / theme.notes.length;
    osc.connect(noteGain);
    noteGain.connect(filter);

    osc.start(now + (i * 0.15));
    nodes.push({ osc, noteGain });
  });

  activeTheme = godId;
  activeNodes = { masterGain, filter, nodes };

  return () => stopGodTheme();
}

/**
 * Stop the currently playing god theme with a smooth fade
 */
export function stopGodTheme() {
  if (!activeNodes || !activeNodes.masterGain) return;

  const ctx = getCtx();
  const now = ctx.currentTime;

  try {
    activeNodes.masterGain.gain.cancelScheduledValues(now);
    activeNodes.masterGain.gain.setValueAtTime(activeNodes.masterGain.gain.value, now);
    activeNodes.masterGain.gain.linearRampToValueAtTime(0, now + 0.5);

    setTimeout(() => {
      try {
        activeNodes.nodes.forEach(({ osc }) => osc.stop());
      } catch (e) { /* already stopped */ }
    }, 600);
  } catch (e) { /* cleanup */ }

  activeTheme = null;
  activeNodes = [];
}

/**
 * Get the currently playing theme god ID
 */
export function getActiveTheme() {
  return activeTheme;
}

// ═══════════════════════════════════════════════════════════════
// SOUND EFFECTS
// ═══════════════════════════════════════════════════════════════

/**
 * Gavel bang — sharp percussive hit for debate start/verdict
 */
export function playGavel(volume = 0.2) {
  const ctx = getCtx();
  const now = ctx.currentTime;

  // Impact body
  const osc = ctx.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(150, now);
  osc.frequency.exponentialRampToValueAtTime(50, now + 0.15);

  const gain = ctx.createGain();
  gain.gain.setValueAtTime(volume * 0.8, now);
  gain.gain.exponentialRampToValueAtTime(0.001, now + 0.3);

  // Noise for wood crack
  const bufferSize = ctx.sampleRate * 0.1;
  const noiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const data = noiseBuffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) {
    data[i] = (Math.random() * 2 - 1) * Math.exp(-i / (bufferSize * 0.05));
  }

  const noise = ctx.createBufferSource();
  noise.buffer = noiseBuffer;

  const noiseGain = ctx.createGain();
  noiseGain.gain.setValueAtTime(volume * 0.4, now);
  noiseGain.gain.exponentialRampToValueAtTime(0.001, now + 0.15);

  const noiseFilter = ctx.createBiquadFilter();
  noiseFilter.type = 'bandpass';
  noiseFilter.frequency.value = 2000;
  noiseFilter.Q.value = 1;

  osc.connect(gain);
  gain.connect(ctx.destination);
  noise.connect(noiseFilter);
  noiseFilter.connect(noiseGain);
  noiseGain.connect(ctx.destination);

  osc.start(now);
  osc.stop(now + 0.35);
  noise.start(now);
  noise.stop(now + 0.2);
}

/**
 * Alliance formed — epic impact with shimmer
 */
export function playAlliance(godId, volume = 0.2) {
  const ctx = getCtx();
  const now = ctx.currentTime;
  const theme = GOD_THEMES[godId] || GOD_THEMES.athena;

  // Deep boom
  const boom = ctx.createOscillator();
  boom.type = 'sine';
  boom.frequency.setValueAtTime(theme.notes[0], now);
  boom.frequency.exponentialRampToValueAtTime(theme.notes[0] / 2, now + 0.5);

  const boomGain = ctx.createGain();
  boomGain.gain.setValueAtTime(volume * 0.6, now);
  boomGain.gain.exponentialRampToValueAtTime(0.001, now + 0.8);

  boom.connect(boomGain);
  boomGain.connect(ctx.destination);

  // Rising shimmer using the god's chord
  theme.notes.forEach((freq, i) => {
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = freq * 2;

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, now + 0.1);
    gain.gain.linearRampToValueAtTime(volume * 0.15, now + 0.3 + (i * 0.1));
    gain.gain.exponentialRampToValueAtTime(0.001, now + 1.5);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start(now + 0.1);
    osc.stop(now + 1.6);
  });

  boom.start(now);
  boom.stop(now + 0.9);
}

/**
 * Verdict delivered — resolution chord
 */
export function playVerdict(volume = 0.15) {
  const ctx = getCtx();
  const now = ctx.currentTime;

  // C major resolution chord
  const chord = [261.63, 329.63, 392, 523.25];

  chord.forEach((freq, i) => {
    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.value = freq;

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(volume * 0.12, now + 0.2 + (i * 0.08));
    gain.gain.linearRampToValueAtTime(volume * 0.08, now + 1.0);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 2.5);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start(now);
    osc.stop(now + 2.6);
  });
}
